import './ConsentedClient.scss';

import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { every, some, sumBy } from 'lodash';
import Store from 'state/Store';
import { getView } from 'state/view/ViewSelectors';
import ProductCard from 'components/shared/ProductCard/ProductCard';
import { ReactComponent as YodleeLogo } from 'assets/logo/logo-yodlee.svg';
import { Dollar } from 'components/shared/Text/Text';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { getActiveVendorSessions } from 'state/session/SessionSelectors';
import ExternalInvestmentProductCardTable from './ExternalInvestmentProductCardTable';
import MobileExternalInvestmentProductCard from './MobileExternalInvestmentProductCardTable';
import { initializeVendorSession } from 'state/session/SessionActions';
import { getExternalInvestmentsBalances } from 'state/external/ExternalInvestmentsBalancesSelectors';

import { getExternalInvestmentConsent } from 'state/external/ExternalInvestmentConsentSelectors';

import { showLinkModal } from 'state/external/ExternalInvestmentConsentActions';

import {
  getExternalInvestmentBalances,
  getExternalInvestmentSource,
  refreshExternalInvestmentBalances
} from 'services/Rest';

import {
  accountCenterLinks,
  accountCenterSettings,
  externalInvestmentLinks,
  linkBuilder,
  yodleeAggregationServiceTerms
} from 'services/Externals';
import LoadingButton from 'components/shared/LoadingButton/LoadingButton';
import {
  getHasReceivedExternalAccountRefresh,
  getHasRequestedExternalAccountRefresh
} from 'state/requests/RequestStatusSelectors';

const ExternalLinkButton = ({ href, handleClick, label }) => {
  if (href) {
    return (
      <a className="external-link-button no-underline" href={href}>
        {label}
      </a>
    );
  }
  return (
    <button className="external-link-button" onClick={handleClick}>
      {label}
    </button>
  );
};

const AccountSettingsLink = () => {
  return (
    <a id="external-account-settings" href={accountCenterSettings}>
      Account Settings
    </a>
  );
};

const ExternalInvestmentTitle = () => (
  <Fragment>
    <p>EXTERNAL INVESTMENT ACCOUNTS</p>
  </Fragment>
);

const ExternalInvestmentCardBottom = ({ children }) => (
  <div className="external-investment-card-bottom">{children}</div>
);

const ExternalInvestmentLogo = () => (
  <YodleeLogo className="external-investment-card-bottom-logo" />
);

const ExternalLinkButtonWithPermissions =
  WithUserPermissions(ExternalLinkButton);
const AccountSettingsLinkWithPermission =
  WithUserPermissions(AccountSettingsLink);

/* Look to do this in the reducer */
export const hasActiveExternalAccountSession = (activeVendorSessions) => {
  const activeVendorSession = activeVendorSessions.filter(
    (s) => s.name === 'externalAccount' && s.active
  );
  return Boolean(activeVendorSession.length);
};

export const activateExternalAccountSession = () => {
  Store.dispatch(
    initializeVendorSession({ name: 'externalAccount', active: true })
  );
};

const TotalExternalAccountBalance = ({ balances, view }) => {
  const allExternalAccountsError = every(
    balances,
    (b) => b.details.allExternalAccountsError
  );
  const subsetExternalAccountsError = some(balances, () =>
    some(balances, (b) => b.details.allExternalAccountsInGroupError)
  );
  const total = sumBy(balances, (o) => o.details.balanceValue);

  return (
    <Fragment>
      <div className="total-external-balance-title">
        Total Balance of External Accounts
      </div>
      {allExternalAccountsError ? (
        <div className="total-external-balance-amount e2e-total-external-balance-amount">
          Currently Unavailable
        </div>
      ) : (
        <Dollar
          element="div"
          className="total-external-balance-amount e2e-total-external-balance-amount">
          {total}
        </Dollar>
      )}
      {subsetExternalAccountsError && (
        <div className="total-external-balance-connection-error">
          Total may not reflect current balances due to unavailable data
        </div>
      )}
      <AccountSettingsLinkWithPermission view={view} shouldDisable />
    </Fragment>
  );
};

const BalanceDisplay = ({ balances, isDesktopLayout, view }) => {
  return (
    <Fragment>
      <TotalExternalAccountBalance balances={balances} view={view} />
      {isDesktopLayout ? (
        <ExternalInvestmentProductCardTable balances={balances} view={view} />
      ) : (
        <MobileExternalInvestmentProductCard balances={balances} view={view} />
      )}
    </Fragment>
  );
};

const openLinkAccount = (
  activeVendorSessions,
  setIframeSource,
  showLinkModal
) => {
  Store.dispatch(showLinkModal(true));
  if (!hasActiveExternalAccountSession(activeVendorSessions)) {
    getExternalInvestmentSource(
      `${externalInvestmentLinks.linkAnAccount}`
    ).then((htmlSource) => {
      activateExternalAccountSession();
      setIframeSource(`data:text/html;charset=utf-8,${escape(htmlSource)}`);
    });
  }
};

const ConsentedClient = ({
  autoOpen,
  activeVendorSessions,
  balances,
  consent,
  setIframeSource,
  view
}) => {
  useEffect(() => {
    if (consent) {
      getExternalInvestmentBalances();
    }
  }, [consent]);

  useEffect(() => {
    if (consent) {
      if (autoOpen === 'LINK') {
        openLinkAccount(activeVendorSessions, setIframeSource, showLinkModal);
      }
      if (autoOpen === 'ACCOUNT_CENTER') {
        const accountCenter = linkBuilder(accountCenterLinks, view);
        window.location.href = accountCenter;
      }
    }
  }, [activeVendorSessions, autoOpen, consent, setIframeSource, view]);

  if (!consent) {
    return null;
  }

  const { isDesktopLayout } = view;
  return (
    <ProductCard
      title={<ExternalInvestmentTitle />}
      className="external-investment-card"
      description=""
      content={
        <BalanceDisplay
          balances={balances}
          isDesktopLayout={isDesktopLayout}
          view={view}
        />
      }>
      <div className="external-investment-footer-container">
        <ExternalInvestmentCardBottom>
          <ExternalLinkButtonWithPermissions
            href={linkBuilder(accountCenterLinks, view)}
            handleClick={activateExternalAccountSession}
            shouldDisable
            styleOverride={{
              display: 'inline'
            }}
            view={view}
            label="Account Center"
          />
          <ExternalLinkButtonWithPermissions
            handleClick={() => {
              openLinkAccount(
                activeVendorSessions,
                setIframeSource,
                showLinkModal
              );
            }}
            label="Link an Account"
            shouldDisable
            styleOverride={{
              display: 'inline'
            }}
            view={view}
          />
          <LoadingButton
            onClick={refreshExternalInvestmentBalances}
            label="Refresh Balance"
            className="external-link-button loadingButtonClass"
            requestStatusKey={'refreshExternalInvestmentBalances'}
            shouldDisable>
            Refresh Balance
          </LoadingButton>
          <div className="external-accounts-terms-of-use">
            <a
              href={yodleeAggregationServiceTerms}
              target="_blank"
              rel="noreferrer">
              Service Terms
            </a>
            <ExternalInvestmentLogo />
          </div>
        </ExternalInvestmentCardBottom>
      </div>
    </ProductCard>
  );
};

const mapStateToProps = (state) => ({
  balances: getExternalInvestmentsBalances(state),
  consent: getExternalInvestmentConsent(state),
  activeVendorSessions: getActiveVendorSessions(state),
  refreshLoading:
    getHasRequestedExternalAccountRefresh(state) &&
    !getHasReceivedExternalAccountRefresh(state),
  view: getView(state)
});

export default connect(mapStateToProps)(ConsentedClient);
